import { MessageSquare, MoreHorizontal, Trash2 } from 'lucide-react'
import { useRef, useState } from 'react'
import type { AgentThreadItem } from '@/services/api/agent'
import { cn } from '@/utils/cn'
import { Button } from '../ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu'

function formatRelative(dateStr: string) {
  const diff = Date.now() - new Date(dateStr).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days === 1) return 'yesterday'
  if (days < 7) return `${days}d ago`
  return new Date(dateStr).toLocaleDateString()
}

export interface AgentThreadListProps {
  threads: AgentThreadItem[]
  activeThreadId: string | undefined
  onSelect: (threadId: string) => void
  onCreate: () => void
  onDelete: (threadId: string) => void
  onRename: (threadId: string, title: string) => void
  isCreating?: boolean
  /**
   * The list sits in the phone thread sheet (AgentThreadSwitcher), where
   * selecting a row closes the sheet. There a tap on a row-menu item must not
   * also select the row, and Rename closes the menu and hands the focus to the
   * title input. Off (the default) keeps the side-column behaviour.
   */
  inSheet?: boolean
}

export function AgentThreadList({
  threads,
  activeThreadId,
  onSelect,
  onCreate,
  onDelete,
  onRename,
  isCreating,
  inSheet = false,
}: AgentThreadListProps) {
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editValue, setEditValue] = useState('')
  const renameInputRef = useRef<HTMLInputElement>(null)

  // Only interactive chats are listed — research/hypothesis run threads are
  // internal artifacts reached through their cards and detail pages
  // (DEEP_RESEARCH_REDESIGN.md phase 4). Deep-linked run threads still open
  // in the main panel; they just have no list row.
  const visibleThreads = threads.filter(t => t.kind === 'chat')

  function startEdit(thread: AgentThreadItem) {
    setEditingId(thread.id)
    setEditValue(thread.title ?? '')
  }

  function commitEdit(threadId: string) {
    if (editValue.trim()) onRename(threadId, editValue.trim())
    setEditingId(null)
  }

  return (
    <div className="flex h-full flex-col border-border bg-card md:border-l">
      {/* Thread list */}
      <div className="flex-1 overflow-y-auto py-1">
        {visibleThreads.length === 0 ? (
          <EmptyState onCreate={onCreate} isCreating={isCreating} />
        ) : (
          visibleThreads.map(thread => {
            const handleClick = () => onSelect(thread.id)
            return (
              <div
                key={thread.id}
                className={cn(
                  'group relative flex items-start gap-2 mx-2 my-0.5 px-2.5 py-2 rounded-md cursor-pointer border border-transparent hover:bg-muted/60 transition-colors',
                  activeThreadId === thread.id &&
                    'border-primary/40 bg-primary/5 hover:bg-primary/5'
                )}
                onClick={handleClick}
              >
                <div className="flex-1 min-w-0">
                  {editingId === thread.id ? (
                    <input
                      ref={renameInputRef}
                      // In the sheet the closing row menu hands the focus over
                      // (see onCloseAutoFocus); focusing on mount would be
                      // pulled back by the still-open menu and commit on blur.
                      // biome-ignore lint/a11y/noAutofocus: intentional for inline rename UX
                      autoFocus={!inSheet}
                      className="w-full text-base md:text-xs bg-background border border-border max-md:focus:border-ring rounded px-1.5 py-0.5 outline-none"
                      value={editValue}
                      onChange={e => setEditValue(e.target.value)}
                      onBlur={() => commitEdit(thread.id)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') commitEdit(thread.id)
                        if (e.key === 'Escape') setEditingId(null)
                      }}
                      onClick={e => e.stopPropagation()}
                    />
                  ) : (
                    <p className="text-sm md:text-xs font-medium text-foreground max-md:line-clamp-2 md:truncate leading-snug">
                      {thread.title ?? 'New conversation'}
                    </p>
                  )}
                  <p className="text-[10px] max-md:text-xs text-muted-foreground mt-0.5">
                    {formatRelative(thread.updated_at)}
                  </p>
                </div>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-9 md:size-5 opacity-0 group-hover:opacity-100 max-md:opacity-100 [@media(hover:none)]:opacity-100 shrink-0 text-muted-foreground hover:text-foreground"
                      onClick={e => e.stopPropagation()}
                    >
                      <MoreHorizontal className="size-4 md:size-3" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    className="w-36"
                    onClick={inSheet ? e => e.stopPropagation() : undefined}
                    onCloseAutoFocus={
                      inSheet
                        ? e => {
                            if (!renameInputRef.current) return
                            e.preventDefault()
                            renameInputRef.current.focus()
                          }
                        : undefined
                    }
                  >
                    <DropdownMenuItem
                      className="max-md:py-2.5 max-md:text-sm"
                      onSelect={e => {
                        if (!inSheet) e.preventDefault()
                        startEdit(thread)
                      }}
                    >
                      Rename
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="text-destructive focus:text-destructive max-md:py-2.5 max-md:text-sm"
                      onSelect={() => onDelete(thread.id)}
                    >
                      <Trash2 className="size-3.5 mr-2" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}

function EmptyState({ onCreate, isCreating }: { onCreate: () => void; isCreating?: boolean }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 py-10 text-center px-4">
      <MessageSquare className="size-6 text-muted-foreground/40" />
      <p className="text-xs text-muted-foreground">No threads yet</p>
      <Button
        variant="outline"
        size="sm"
        className="max-md:h-10"
        onClick={onCreate}
        disabled={isCreating}
      >
        Start conversation
      </Button>
    </div>
  )
}
